import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  onIdTokenChanged,
  getIdTokenResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  SAMLAuthProvider,
  type User,
} from 'firebase/auth'
import { auth } from '@/firebase/config'
import { getFunctions, httpsCallable } from 'firebase/functions'
import type { AuthzClaims, AuthzRole, DecodedTokenLike } from '@/types/AuthzClaims'
import { isRBACEnabled, getFallbackRoles, logRBACConfig } from '@/config/rbac'
import { getTenantSearch } from '@/services/serviceFactory'
import type { Tenant } from '@/types/Tenant'
import { useLocalSettingsStore } from '@/stores/localSettingsStore'

export const useAuthStore = defineStore(
  'auth',
  () => {
    // Firebase user state
    const firebaseUser = ref<User | null>(null)
    const loading = ref(true)
    const error = ref<string | null>(null)

    // Authorization claims state
    const claims = ref<AuthzClaims>({ roles: [], authzVer: undefined })

    // Local settings for session context
    const localSettings = useLocalSettingsStore()

    // Computed properties for roles and scope
    const roles = computed<AuthzRole[]>(() => {
      if (!isRBACEnabled()) {
        return getFallbackRoles() as AuthzRole[]
      }
      return claims.value.roles
    })

    const isAuthenticated = computed<boolean>(() => !!firebaseUser.value)

    // Add new computed properties for Identity Platform tenant
    const isSaasAdmin = computed(() => claims.value.is_saas_admin === true)
    const authTenant = computed(() => claims.value.auth_tenant || null)
    const hasAuthTenant = computed(() => !!authTenant.value)

    // Check if we have valid persisted authentication state
    const hasPersistedAuth = computed<boolean>(() => {
      return (
        !loading.value &&
        (!!firebaseUser.value ||
          (claims.value.roles.length > 0 && isRBACEnabled()) ||
          (!isRBACEnabled() && getFallbackRoles().length > 0))
      )
    })

    // Session context using authTenant directly
    const session = computed(() => ({
      tenantId: authTenant.value || undefined,
      yardId: localSettings.settings.selectedScaleDeskId || undefined,
      userId: firebaseUser.value?.uid || undefined,
    }))

    // Current tenant (placeholder - would need to be implemented based on your tenant resolution logic)
    const currentTenant = computed<Tenant | null>(() => {
      // This would need to be implemented based on your tenant resolution logic
      // For now, return null as a placeholder
      return null
    })

    // Authorization methods
    async function refreshClaims(user: User) {
      const token = await getIdTokenResult(user, true)
      const decoded = token.claims as DecodedTokenLike
      const authTenantId =
        (token.claims as { firebase?: { tenant?: string } }).firebase?.tenant || null

      claims.value = {
        roles: decoded.roles ?? [],
        authzVer: decoded.authzVer,
        is_saas_admin: decoded.is_saas_admin,
        auth_tenant: authTenantId,
      }
    }

    function hasRole(role: AuthzRole): boolean {
      return roles.value.includes(role)
    }

    // Simple role checker computed properties
    const isTenantAdmin = computed(() => hasRole('tenantAdmin'))
    const isCashier = computed(() => hasRole('cashier'))
    const isScaleOperator = computed(() => hasRole('scaleOperator'))
    const isScaleMaster = computed(() => hasRole('scaleMaster'))

    // User management methods
    async function register(email: string, password: string) {
      try {
        error.value = null
        await createUserWithEmailAndPassword(auth, email, password)
      } catch (e) {
        error.value = e instanceof Error ? e.message : String(e)
        throw e
      }
    }

    // Resolve tenant by email domain
    async function resolveTenantByEmail(email: string): Promise<Tenant | null> {
      try {
        const functions = getFunctions()
        const resolveTenant = httpsCallable(functions, 'resolveTenant')

        const result = await resolveTenant({ email })
        const data = result.data as {
          authTenantId: string
          slug: string
          displayName: string
          samlProviderIds?: string[]
        }

        // Convert to Tenant object format
        return {
          id: data.authTenantId,
          slug: data.slug,
          displayName: data.displayName,
          emailDomains: [],
          auth: {
            tenantId: data.authTenantId,
            providers: data.samlProviderIds || [],
            authorizedDomains: [],
          },
          subscriptionTier: null,
          createdAt: new Date(),
          updatedAt: new Date(),
          isDeleted: false,
        }
      } catch (e) {
        console.error('Error resolving tenant by email:', e)
        return null
      }
    }

    // Resolve tenant by slug
    async function resolveTenantBySlug(slug: string): Promise<Tenant | null> {
      try {
        const functions = getFunctions()
        const resolveTenant = httpsCallable(functions, 'resolveTenant')

        const result = await resolveTenant({ slug })
        const data = result.data as {
          authTenantId: string
          slug: string
          displayName: string
          samlProviderIds?: string[]
        }

        // Convert to Tenant object format
        return {
          id: data.authTenantId,
          slug: data.slug,
          displayName: data.displayName,
          emailDomains: [],
          auth: {
            tenantId: data.authTenantId,
            providers: data.samlProviderIds || [],
            authorizedDomains: [],
          },
          subscriptionTier: null,
          createdAt: new Date(),
          updatedAt: new Date(),
          isDeleted: false,
        }
      } catch (e) {
        console.error('Error resolving tenant by slug:', e)
        return null
      }
    }

    // Fetch available sign-in methods for an email
    async function getSignInMethods(
      email: string,
      tenantSlug?: string,
    ): Promise<{ methods: string[]; samlProviderIds: string[] }> {
      try {
        console.log('🔐 AuthStore: Fetching sign-in methods', { email, tenantSlug })
        error.value = null

        let tenant: Tenant | null = null

        // Resolve tenant by slug if provided, otherwise by email domain
        if (tenantSlug) {
          console.log('🔐 AuthStore: Resolving tenant by slug', tenantSlug)
          tenant = await resolveTenantBySlug(tenantSlug)
        } else {
          console.log('🔐 AuthStore: Resolving tenant by email domain')
          tenant = await resolveTenantByEmail(email)
        }

        if (!tenant) {
          throw new Error('No tenant found for this email address')
        }

        // Set the tenant context before fetching sign-in methods
        console.log('🔐 AuthStore: Setting tenant context', tenant.auth.tenantId)
        ;(auth as { tenantId?: string }).tenantId = tenant.auth.tenantId

        // Extract SAML provider IDs from tenant auth.providers first
        const samlProviderIds = tenant.auth.providers.filter((p) => p.startsWith('saml.'))

        // Try to fetch sign-in methods, but don't fail if it returns empty and we have SAML providers
        let signInMethods: string[] = []
        try {
          console.log('🔐 AuthStore: Calling fetchSignInMethodsForEmail')
          signInMethods = await fetchSignInMethodsForEmail(auth, email)
          console.log('🔐 AuthStore: Sign-in methods fetched', signInMethods)
        } catch (e) {
          console.warn(
            '🔐 AuthStore: Error fetching sign-in methods, but continuing with SAML providers',
            e,
          )
          // If we have SAML providers, we can still allow sign-in
          if (samlProviderIds.length === 0) {
            throw e // Only throw if we don't have SAML providers as fallback
          }
        }

        // If fetchSignInMethodsForEmail returned empty but we have SAML providers, allow sign-in anyway
        if (signInMethods.length === 0 && samlProviderIds.length > 0) {
          console.log(
            '🔐 AuthStore: No methods from fetchSignInMethodsForEmail, but SAML providers available',
          )
        }

        return {
          methods: signInMethods,
          samlProviderIds: samlProviderIds,
        }
      } catch (e) {
        console.error('🔐 AuthStore: Error fetching sign-in methods', e)
        error.value = e instanceof Error ? e.message : String(e)
        throw e
      }
    }

    // Login with email/password and tenant resolution
    async function login(email: string, password: string, tenantSlug?: string) {
      try {
        console.log('🔐 AuthStore: Starting login', { email, tenantSlug })
        error.value = null

        let tenant: Tenant | null = null

        // Resolve tenant by slug if provided, otherwise by email domain
        if (tenantSlug) {
          console.log('🔐 AuthStore: Resolving tenant by slug', tenantSlug)
          tenant = await resolveTenantBySlug(tenantSlug)
        } else {
          console.log('🔐 AuthStore: Resolving tenant by email domain')
          tenant = await resolveTenantByEmail(email)
        }

        console.log('🔐 AuthStore: Tenant resolved', {
          tenant: tenant
            ? { id: tenant.id, slug: tenant.slug, authTenantId: tenant.auth.tenantId }
            : null,
        })

        // Debug: Log the full tenant object
        if (tenant) {
          console.log('🔐 AuthStore: Full tenant object', tenant)
        }

        if (!tenant) {
          throw new Error('No tenant found for this email address')
        }

        // Set the tenant context before signing in
        console.log('🔐 AuthStore: Setting tenant context', tenant.auth.tenantId)
        ;(auth as { tenantId?: string }).tenantId = tenant.auth.tenantId

        console.log('🔐 AuthStore: Calling Firebase signInWithEmailAndPassword')
        await signInWithEmailAndPassword(auth, email, password)
        console.log('🔐 AuthStore: Firebase signIn completed')

        // Set the actual tenant ID from the tenant data we already have
        console.log('🔐 AuthStore: Tenant resolved', tenant.id)
      } catch (e) {
        console.error('🔐 AuthStore: Login error', e)
        error.value = e instanceof Error ? e.message : String(e)
        throw e
      }
    }

    // Sign in with Google
    async function signInWithGoogle(email: string, tenantSlug?: string) {
      try {
        console.log('🔐 AuthStore: Starting Google sign-in', { email, tenantSlug })
        error.value = null

        let tenant: Tenant | null = null

        // Resolve tenant by slug if provided, otherwise by email domain
        if (tenantSlug) {
          console.log('🔐 AuthStore: Resolving tenant by slug', tenantSlug)
          tenant = await resolveTenantBySlug(tenantSlug)
        } else {
          console.log('🔐 AuthStore: Resolving tenant by email domain')
          tenant = await resolveTenantByEmail(email)
        }

        if (!tenant) {
          throw new Error('No tenant found for this email address')
        }

        // Set the tenant context before signing in
        console.log('🔐 AuthStore: Setting tenant context', tenant.auth.tenantId)
        ;(auth as { tenantId?: string }).tenantId = tenant.auth.tenantId

        console.log('🔐 AuthStore: Calling Firebase signInWithPopup with Google')
        const provider = new GoogleAuthProvider()
        await signInWithPopup(auth, provider)
        console.log('🔐 AuthStore: Google sign-in completed')
      } catch (e) {
        console.error('🔐 AuthStore: Google sign-in error', e)
        error.value = e instanceof Error ? e.message : String(e)
        throw e
      }
    }

    // Sign in with SAML using Redirect (required for Safari mobile compatibility)
    async function signInWithSAML(email: string, samlProviderId: string, tenantSlug?: string) {
      try {
        console.log('🔐 AuthStore: Starting SAML sign-in with REDIRECT', {
          email,
          samlProviderId,
          tenantSlug,
        })
        error.value = null

        let tenant: Tenant | null = null

        // Resolve tenant by slug if provided, otherwise by email domain
        if (tenantSlug) {
          console.log('🔐 AuthStore: Resolving tenant by slug', tenantSlug)
          tenant = await resolveTenantBySlug(tenantSlug)
        } else {
          console.log('🔐 AuthStore: Resolving tenant by email domain')
          tenant = await resolveTenantByEmail(email)
        }

        if (!tenant) {
          throw new Error('No tenant found for this email address')
        }

        // Set the tenant context before signing in
        console.log('🔐 AuthStore: Setting tenant context', tenant.auth.tenantId)
        ;(auth as { tenantId?: string }).tenantId = tenant.auth.tenantId

        // Store tenant ID in localStorage so we can restore it after redirect
        localStorage.setItem('pendingAuthTenantId', tenant.auth.tenantId)

        console.log('🔐 AuthStore: Calling Firebase signInWithRedirect with SAML', samlProviderId)
        const provider = new SAMLAuthProvider(samlProviderId)
        await signInWithRedirect(auth, provider)
        console.log('🔐 AuthStore: SAML sign-in redirect initiated')
        // Execution stops here. The user is redirected away.
      } catch (e) {
        console.error('🔐 AuthStore: SAML sign-in error', e)
        error.value = e instanceof Error ? e.message : String(e)
        throw e
      }
    }

    // Logout
    async function logout() {
      try {
        // Store current tenant info before logout
        const currentTenantSlug = authTenant.value
          ? await getTenantSlugById(authTenant.value)
          : null

        // Clear any pending redirect check to prevent re-authentication after logout
        pendingRedirectCheck = null
        localStorage.removeItem('pendingAuthTenantId')

        await signOut(auth)

        // Return tenant slug for redirect
        return currentTenantSlug
      } catch (e) {
        error.value = e instanceof Error ? e.message : String(e)
        throw e
      }
    }

    // Helper function to get tenant slug by ID
    async function getTenantSlugById(tenantId: string): Promise<string | null> {
      try {
        const search = await getTenantSearch({})
        const tenants = await search.byId(tenantId).run()
        return tenants.length > 0 ? tenants[0].slug || null : null
      } catch (error) {
        console.error('Error getting tenant slug by ID:', error)
        return null
      }
    }

    // Early initialization check variable (must be declared before handleRedirectResult)
    // This must execute BEFORE onIdTokenChanged is set up, as Firebase might process
    // the token and update the user immediately upon receiving the redirect
    let pendingRedirectCheck: Promise<boolean> | null = null

    // Handle redirect result (for SAML and other redirect-based sign-ins)
    // This function waits for the early initialization check and reports success
    async function handleRedirectResult() {
      console.log('🔐 AuthStore: handleRedirectResult called', {
        hasPendingCheck: !!pendingRedirectCheck,
        currentUser: auth.currentUser ? { uid: auth.currentUser.uid } : null,
        tenantId: (auth as { tenantId?: string }).tenantId,
      })

      // 1. Await the early initialization check
      if (pendingRedirectCheck) {
        console.log('🔐 AuthStore: Awaiting pending redirect check...')
        const processed = await pendingRedirectCheck
        console.log('🔐 AuthStore: Pending redirect check completed', { processed })
        // If the early check returned true, the user is signed in.
        if (processed || auth.currentUser) {
          console.log('🔐 AuthStore: Redirect result processed successfully from early check')

          // Wait for claims to be refreshed before returning success
          // This ensures hasAuthTenant is set before the router redirects
          if (isRBACEnabled()) {
            console.log('🔐 AuthStore: Waiting for claims to be refreshed...')
            // Wait up to 5 seconds for claims to be refreshed
            const maxWait = 5000
            const startTime = Date.now()
            while (!hasAuthTenant.value && Date.now() - startTime < maxWait) {
              await new Promise((resolve) => setTimeout(resolve, 100))
            }
            if (hasAuthTenant.value) {
              console.log('🔐 AuthStore: Claims refreshed, hasAuthTenant is now true')
            } else {
              console.warn(
                '🔐 AuthStore: Timeout waiting for claims refresh, but proceeding anyway',
              )
            }
          }

          return true
        }
      } else {
        console.log('🔐 AuthStore: No pending redirect check found')
      }

      // 2. If the user is signed in (e.g., if the automatic process already finished), exit successfully
      if (auth.currentUser) {
        console.log(
          '🔐 AuthStore: User already authenticated, redirect may have been processed automatically',
        )

        // Wait for claims to be refreshed before returning success
        if (isRBACEnabled()) {
          console.log('🔐 AuthStore: Waiting for claims to be refreshed...')
          const maxWait = 5000
          const startTime = Date.now()
          while (!hasAuthTenant.value && Date.now() - startTime < maxWait) {
            await new Promise((resolve) => setTimeout(resolve, 100))
          }
          if (hasAuthTenant.value) {
            console.log('🔐 AuthStore: Claims refreshed, hasAuthTenant is now true')
          } else {
            console.warn('🔐 AuthStore: Timeout waiting for claims refresh, but proceeding anyway')
          }
        }

        localStorage.removeItem('pendingAuthTenantId')
        return true
      }

      // 3. If we get here, the redirect flow failed silently.
      console.log('🔐 AuthStore: Redirect resolution failed or not applicable.', {
        hasPendingCheck: !!pendingRedirectCheck,
        currentUser: auth.currentUser,
        tenantId: (auth as { tenantId?: string }).tenantId,
      })
      localStorage.removeItem('pendingAuthTenantId')

      // Note: We should NOT call getRedirectResult(auth) here again,
      // as it might already have been called once in pendingRedirectCheck
      // and Firebase often returns null on the second call.

      return false
    }

    // Early initialization check (before auth listeners)
    // This must execute BEFORE onIdTokenChanged is set up
    if (typeof window !== 'undefined') {
      // Derive tenantId from URL parameters OR localStorage
      const urlParams = new URLSearchParams(window.location.search)
      const tenantIdFromUrl = urlParams.get('tid')
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
      const tenantIdFromHash = hashParams.get('tid')
      const storedTenantId = localStorage.getItem('pendingAuthTenantId')

      const tenantId = tenantIdFromUrl || tenantIdFromHash || storedTenantId

      if (tenantId) {
        console.log('🔐 AuthStore: Setting tenant context early from', {
          source: tenantIdFromUrl ? 'URL' : tenantIdFromHash ? 'URL hash' : 'localStorage',
          tenantId,
        })
        // A. CRITICAL: Set the tenant context immediately
        ;(auth as { tenantId?: string }).tenantId = tenantId

        pendingRedirectCheck = (async () => {
          try {
            console.log('🔐 AuthStore: Early redirect check starting, calling getRedirectResult', {
              tenantId: (auth as { tenantId?: string }).tenantId,
              currentUser: auth.currentUser ? { uid: auth.currentUser.uid } : null,
            })
            if (typeof window !== 'undefined') {
              const sessionStorageKeys: string[] = []
              for (let i = 0; i < window.sessionStorage.length; i += 1) {
                const key = window.sessionStorage.key(i)
                if (key) {
                  sessionStorageKeys.push(key)
                }
              }
              const localStorageKeys: string[] = []
              for (let i = 0; i < window.localStorage.length; i += 1) {
                const key = window.localStorage.key(i)
                if (key) {
                  localStorageKeys.push(key)
                }
              }
              console.log('🔐 AuthStore: Storage snapshot before getRedirectResult', {
                sessionStorageKeys,
                localStorageKeys,
                hasPendingRedirectKey:
                  window.sessionStorage.getItem('firebase:pendingRedirectKey') !== null,
              })
            }
            // B. Run getRedirectResult() with the tenant context set
            const result = await getRedirectResult(auth)

            console.log('🔐 AuthStore: getRedirectResult returned', {
              hasResult: !!result,
              user: result?.user ? { uid: result.user.uid, email: result.user.email } : null,
              providerId: result?.providerId,
            })

            // C. Clean up regardless of result
            localStorage.removeItem('pendingAuthTenantId')

            if (result) {
              console.log('🔐 AuthStore: Redirect result found during early initialization', {
                user: result.user ? { uid: result.user.uid, email: result.user.email } : null,
                providerId: result.providerId,
              })
              // D. This processes the SAML response and triggers onIdTokenChanged
              return true
            } else {
              console.log('🔐 AuthStore: getRedirectResult returned null (no pending redirect)')
            }
            return false
          } catch (e) {
            console.error('🔐 AuthStore: Error in early redirect check', e)
            localStorage.removeItem('pendingAuthTenantId')
            return false
          }
        })()
      }
    }

    // Initialize auth state listeners
    onAuthStateChanged(auth, async (user) => {
      firebaseUser.value = user
      loading.value = false
    })

    onIdTokenChanged(auth, async (user) => {
      console.log('🔐 AuthStore: onIdTokenChanged triggered', {
        user: user ? { uid: user.uid, email: user.email } : null,
        isRBACEnabled: isRBACEnabled(),
      })

      firebaseUser.value = user
      if (user) {
        if (isRBACEnabled()) {
          console.log('🔐 AuthStore: RBAC enabled, refreshing claims')
          await refreshClaims(user)
          console.log('🔐 AuthStore: Claims refreshed', {
            roles: claims.value.roles,
            is_saas_admin: claims.value.is_saas_admin,
            auth_tenant: claims.value.auth_tenant,
          })
        } else {
          console.log('🔐 AuthStore: RBAC disabled, using fallback values')
          // When RBAC is disabled, use fallback values
          claims.value = {
            roles: getFallbackRoles() as AuthzRole[],
            authzVer: undefined,
            is_saas_admin: false,
            auth_tenant: null,
          }
          // Clear actual tenant ID when RBAC is disabled
        }
      } else {
        console.log('🔐 AuthStore: User logged out, clearing claims')
        // User logged out - clear claims
        claims.value = {
          roles: [],
          authzVer: undefined,
          is_saas_admin: false,
          auth_tenant: null,
        }
        // Clear actual tenant ID on logout
      }
      loading.value = false
      console.log('🔐 AuthStore: Auth state updated', {
        loading: loading.value,
        isAuthenticated: !!firebaseUser.value,
        hasPersistedAuth:
          !loading.value &&
          (!!firebaseUser.value ||
            (claims.value.roles.length > 0 && isRBACEnabled()) ||
            (!isRBACEnabled() && getFallbackRoles().length > 0)),
      })

      // Log RBAC config in debug mode
      logRBACConfig()
    })

    return {
      // state
      firebaseUser,
      claims,
      loading,
      error,
      // getters
      roles,
      isAuthenticated,
      hasPersistedAuth,
      isSaasAdmin,
      authTenant,
      hasAuthTenant,
      session,
      currentTenant,
      // actions
      refreshClaims,
      hasRole,
      // role checkers
      isTenantAdmin,
      isCashier,
      isScaleOperator,
      isScaleMaster,
      register,
      login,
      signInWithGoogle,
      signInWithSAML,
      getSignInMethods,
      handleRedirectResult,
      logout,
      resolveTenantByEmail,
      resolveTenantBySlug,
    }
  },
  {
    // @ts-ignore - persist option is added by pinia-plugin-persistedstate but types aren't updated
    persist: {
      storage: localStorage,
      // Don't persist firebaseUser - it can't be serialized and will be restored from Firebase Auth
      // Don't persist loading state - it should always start as true
      // Don't persist computed values - they're derived from persisted state
    },
  },
)

// Helper composable for session
export const useSession = () => {
  const store = useAuthStore()
  return store.session
}
